import { createMutationHook, createQueryHook } from "../factory";

export const batchClaimKeys = {
  all: ["batchClaims"] as const,
  details: () => [...batchClaimKeys.all, "detail"] as const,
  detail: ({
    batchClaimId,
    includeAdminFields,
  }: {
    batchClaimId: string;
    includeAdminFields: boolean;
  }) =>
    [...batchClaimKeys.details(), batchClaimId, includeAdminFields] as const,
  headerInfo: ({
    batchClaimId,
    shouldShowExtendedFields,
  }: {
    batchClaimId: string;
    shouldShowExtendedFields: boolean;
  }) =>
    [
      ...batchClaimKeys.details(),
      batchClaimId,
      shouldShowExtendedFields,
      "headerInfo",
    ] as const,
  auditDocumentation: ({ batchClaimId }: { batchClaimId: string }) =>
    [...batchClaimKeys.details(), batchClaimId, "auditDocumentation"] as const,
  comments: ({ batchClaimId }: { batchClaimId: string }) =>
    [...batchClaimKeys.details(), batchClaimId, "comments"] as const,
  eventHistoryTimeline: ({ batchClaimId }: { batchClaimId: string }) =>
    [
      ...batchClaimKeys.details(),
      batchClaimId,
      "eventHistoryTimeline",
    ] as const,
  documentationState: ({ batchClaimIds }: { batchClaimIds: string[] }) =>
    [...batchClaimKeys.all, "documentationState", batchClaimIds] as const,
  roleAssignments: ({ claimId }: { claimId: string }) =>
    [...batchClaimKeys.details(), claimId, "roleAssignments"] as const,
};

const claimAuditTypesKeys = {
  all: ["claimAuditTypes"] as const,
  lists: () => [...claimAuditTypesKeys.all, "list"] as const,
  list: <T>(filters: T) => [...claimAuditTypesKeys.lists(), filters] as const,
};

const claimPhasesKeys = {
  all: ["claimPhases"] as const,
  lists: () => [...claimPhasesKeys.all, "list"] as const,
  list: <T>(filters: T) => [...claimPhasesKeys.lists(), filters] as const,
};

const claimsSummaryKeys = {
  all: ["claimsSummary"] as const,
  detail: ({
    batchClaimFilter,
    providerOnly,
  }: {
    batchClaimFilter: unknown;
    providerOnly?: boolean;
  }) =>
    [
      ...claimsSummaryKeys.all,
      batchClaimFilter,
      providerOnly ?? false,
    ] as const,
};

export const batchClaims = {
  createBatchClaimComment: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.createBatchClaimComment,
    mutationKey: ["batchClaims", "createBatchClaimComment"],
  }),
  createDisputedFindings: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.createDisputedFindings,
    mutationKey: ["batchClaims", "createDisputedFindings"],
  }),
  createUbClaimFindings: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.createUbClaimFindings,
    mutationKey: ["batchClaims", "createUbClaimFindings"],
  }),
  getBatchClaimAssigneeAuthorAndProviderInfo: createQueryHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.getBatchClaimAssigneeAuthorAndProviderInfo,
    queryKeyFn: batchClaimKeys.detail,
  }),
  getBatchClaimAuditDocumentation: createQueryHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.getBatchClaimAuditDocumentation,
    queryKeyFn: batchClaimKeys.auditDocumentation,
  }),
  getBatchClaimComments: createQueryHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.getBatchClaimComments,
    queryKeyFn: batchClaimKeys.comments,
  }),
  getBatchClaimEventHistoryTimeline: createQueryHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.getBatchClaimEventHistoryTimeline,
    queryKeyFn: batchClaimKeys.eventHistoryTimeline,
  }),
  getBatchClaimHeaderInfo: createQueryHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.getBatchClaimHeaderInfo,
    queryKeyFn: batchClaimKeys.headerInfo,
  }),
  batchClaimDocumentationState: createQueryHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.batchClaimDocumentationState,
    queryKeyFn: batchClaimKeys.documentationState,
  }),
  getClaimAuditTypes: createQueryHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.getClaimAuditTypes,
    queryKeyFn: claimAuditTypesKeys.list,
  }),
  getClaimRoleAssignments: createQueryHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.getClaimRoleAssignments,
    queryKeyFn: batchClaimKeys.roleAssignments,
  }),
  getClaimPhases: createQueryHook({
    methodSelector: (alaffiaClient) => alaffiaClient.batchClaims.getClaimPhases,
    queryKeyFn: claimPhasesKeys.list,
  }),
  getClaimLifecycleAggregates: createQueryHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.getClaimLifecycleAggregates,
    queryKeyFn: claimsSummaryKeys.detail,
  }),
  openStageTrackerBlocker: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.openStageTrackerBlocker,
    mutationKey: ["batchClaims", "openStageTrackerBlocker"],
  }),
  deleteStageTrackerBlocker: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.deleteStageTrackerBlocker,
    mutationKey: ["batchClaims", "deleteStageTrackerBlocker"],
  }),
  setStageTrackerBlockerSegment: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.setStageTrackerBlockerSegment,
    mutationKey: ["batchClaims", "setStageTrackerBlockerSegment"],
  }),
  setAssignees: createMutationHook({
    methodSelector: (alaffiaClient) => alaffiaClient.batchClaims.setAssignees,
    mutationKey: ["batchClaims", "setAssignees"],
  }),
  setClaimAuditTypes: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.setClaimAuditTypes,
    mutationKey: ["batchClaims", "setClaimAuditTypes"],
  }),
  setClaimAppealed: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.setClaimAppealed,
    mutationKey: ["batchClaims", "setClaimAppealed"],
  }),
  setClaimDisputed: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.setClaimDisputed,
    mutationKey: ["batchClaims", "setClaimDisputed"],
  }),
  setClaimStage: createMutationHook({
    methodSelector: (alaffiaClient) => alaffiaClient.batchClaims.setClaimStage,
    mutationKey: ["batchClaims", "setClaimStage"],
  }),
  setClaimTechDenials: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.setClaimTechDenials,
    mutationKey: ["batchClaims", "setClaimTechDenials"],
  }),
  setClientSignOffClaimStates: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.setClientSignOffClaimStates,
    mutationKey: ["batchClaims", "setClientSignOffClaimStates"],
  }),
  setDocumentationClaimStates: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.setDocumentationClaimStates,
    mutationKey: ["batchClaims", "setDocumentationClaimStates"],
  }),
  setDueDates: createMutationHook({
    methodSelector: (alaffiaClient) => alaffiaClient.batchClaims.setDueDates,
    mutationKey: ["batchClaims", "setDueDates"],
  }),
  setFindingStatus: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.setFindingStatus,
    mutationKey: ["batchClaims", "setFindingStatus"],
  }),
  setInvoiceClaimStates: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.setInvoiceClaimStates,
    mutationKey: ["batchClaims", "setInvoiceClaimStates"],
  }),
  setNegotiationClaimStates: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.setNegotiationClaimStates,
    mutationKey: ["batchClaims", "setNegotiationClaimStates"],
  }),
  setOnHoldClaimStates: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.setOnHoldClaimStates,
    mutationKey: ["batchClaims", "setOnHoldClaimStates"],
  }),
  setProviderSignOffClaimStates: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.setProviderSignOffClaimStates,
    mutationKey: ["batchClaims", "setProviderSignOffClaimStates"],
  }),
  setWorkflowClaimStates: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.setWorkflowClaimStates,
    mutationKey: ["batchClaims", "setWorkflowClaimStates"],
  }),
  saveClaimRoleAssignments: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.batchClaims.saveClaimRoleAssignments,
    mutationKey: ["batchClaims", "saveClaimRoleAssignments"],
  }),
};
